// Dictionary, 844 part 1 — the 844-specific segments with no shared
// equivalent: BAA (Beginning Segment for Product Transfer Account
// Adjustment), CON (contract reference) and PAD (Product Transfer Detail).
// Generic X12 844 (005010) definitions only; not tied to any trading
// partner's implementation guide. Sourced from
// reference/session/844-product-transfer-account-adjustment-qualifiers-txt.md.
//
// REF, DTM, N1-N9, PER, QTY, AMT and CTP all recur inside the 844 too, but
// X12 defines one segment per id — they're already defined in
// dictionary/header-segments.ts, dictionary/item-loop-segments.ts,
// dictionary/852-item-activity-segments.ts and
// dictionary/quantity-qualifier-codes.ts /
// dictionary/amount-qualifier-codes.ts, and merged in by index.ts, so they
// are not redefined here.

import {LoopId, type ElementDefinition, type SegmentDefinition} from './types';
import {TRANSACTION_SET_PURPOSE_CODES} from './transaction-set-purpose-codes';
import {REFERENCE_IDENTIFICATION_QUALIFIER_CODES} from './reference-identification-qualifiers';
import {CONTRACT_STATUS_CODES} from './contract-status-codes';
import {PRODUCT_TRANSFER_TYPE_CODES} from './product-transfer-type-codes';
import {CHANGE_OR_RESPONSE_TYPE_CODES} from './change-or-response-type-codes';

function el(
  position: number,
  label: string,
  description: string,
  extra: Partial<ElementDefinition> = {}
): ElementDefinition {
  return {position, label, description, ...extra};
}

export const SEGMENTS_844: Record<string, SegmentDefinition> = {
  BAA: {
    id: 'BAA',
    name: 'Beginning Segment for Product Transfer Account Adjustment',
    purpose:
      'Identifies what kind of adjustment this is (debit memo, credit memo, etc.) and, optionally, a reference it relates to. Required at the start of every 844.',
    loop: LoopId.Header,
    required: true,
    elements: [
      el(
        1,
        'Transaction Set Purpose Code',
        'Whether this is an original claim, a cancellation, a replacement, etc.',
        {
          dataType: 'code',
          required: true,
          codes: TRANSACTION_SET_PURPOSE_CODES,
        }
      ),
      el(
        2,
        'Transaction Type Code',
        'Whether this claim is a debit memo, credit memo, request for credit, or debit invoice.',
        {
          dataType: 'code',
          required: true,
          codes: [
            {code: 'DR', label: 'Debit Memo'},
            {code: 'CR', label: 'Credit Memo'},
            {code: 'RA', label: 'Request for Credit'},
            {code: 'DI', label: 'Debit Invoice'},
          ],
        }
      ),
      el(3, 'Date', 'Date the claim/adjustment was prepared.', {
        dataType: 'date',
        format: 'CCYYMMDD',
        required: true,
      }),
      el(
        4,
        'Reference Identification Qualifier',
        'What kind of reference number follows, when one is given.',
        {
          dataType: 'code',
          codes: REFERENCE_IDENTIFICATION_QUALIFIER_CODES,
        }
      ),
      el(
        5,
        'Reference Identification',
        'A reference number this claim relates to, when applicable.'
      ),
    ],
  },

  CON: {
    id: 'CON',
    name: 'Contract Reference',
    purpose:
      'Carries a contract number this claim relates to and its current status.',
    loop: LoopId.Header,
    elements: [
      el(
        1,
        'Reference Identification Qualifier',
        'What kind of reference number follows.',
        {
          dataType: 'code',
          required: true,
          codes: REFERENCE_IDENTIFICATION_QUALIFIER_CODES,
        }
      ),
      el(
        2,
        'Reference Identification',
        'The contract/agreement/quote number.',
        {
          required: true,
        }
      ),
      el(3, 'Contract Status Code', 'The current status of that contract.', {
        dataType: 'code',
        codes: CONTRACT_STATUS_CODES,
      }),
    ],
  },

  PAD: {
    id: 'PAD',
    name: 'Product Transfer Detail',
    purpose:
      'Opens one claim/adjustment detail line: what kind of transfer this is and what kind of change is being made. Every claim/adjustment detail loop starts with a PAD.',
    loop: LoopId.Item,
    required: true,
    elements: [
      el(
        1,
        'Assigned Identification',
        'The claim/adjustment line number, assigned by the party submitting the claim.'
      ),
      el(
        2,
        'Product Transfer Type Code',
        'What kind of sale or transfer this claim line covers.',
        {
          dataType: 'code',
          required: true,
          codes: PRODUCT_TRANSFER_TYPE_CODES,
        }
      ),
      el(
        3,
        'Change or Response Type Code',
        'What kind of change is being made to this claim line.',
        {
          dataType: 'code',
          codes: CHANGE_OR_RESPONSE_TYPE_CODES,
        }
      ),
    ],
  },
};
